import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

// Actions
import {
  getCampaignMediaByCity,
  updateCampaignMediaDuration,
} from "../../../actions/campaign-media/CampaignMediaActions";
import { getCityOverview } from "../../../actions/campaign-report/CampaignCityReportActions";

// Constants and Utils
import { getModifiedPopulation } from "../../../utils/ReachAndFrequencyUtils";
import { constructRedirectPath } from "../../../utils/redirect-utils/RedirectUtils";
import { DefaultPagination } from "../../../constants/GeneralConstants";

// Urls
import { RedirectTo } from "../../../urls/PageLinksURL";

// Component
import Spinner from "../../../components/spinner/Spinner";
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import CampaignSidebar from "../../../components/campaign-sidebar/CampaignSidebar";
import TableHeaders from "../../../components/table/TableHeaders";
import {
  FrequencyCell,
  ImpressionsCell,
  InfoCell,
  LtsCell,
  ReachCell,
} from "../../../components/campaign-media-table-row/MediaRow";
import { DurationCell } from "../../../components/campaign-table-row/CampaignRowCells";
import AlertMessage from "../../../mavin/components/alert-message/AlertMessage";
import { CampaignCityPerformance } from "../../../mavin/components/campaign-city-performance/CampaignCityPerformance";
import { OtsAndReachHeader } from "../../../components/table/OtsAndReachHeader";
import NoTableDataMessage from "../../../components/table/NoTableDataMessage";

// Page Constants
const otsHeaderInfo = [
  {
    displayText: "TG Impressions",
    className: "bg-light-green",
    percentage: "25",
  },
  {
    displayText: "Total Impressions",
    className: "bg-light-blue",
    percentage: "100",
  },
];

const reachHeaderInfo = [
  {
    displayText: "TG Reach",
    className: "bg-light-pink",
    percentage: "25",
  },
  {
    displayText: "Total Reach",
    className: "bg-light-yellow",
    percentage: "100",
  },
];

const tableHeaders = (cityPopulation) => [
  {
    title: {
      displayName: "Site Name",
      className: "col-4 align-middle",
    },
    subTitle: {
      displayName: "Media type, Dimensions, Lighting, Region",
      className: "",
    },
  },
  {
    title: {
      displayName: "Start and End Date",
      className: "col-2 align-middle text-right",
    },
    subTitle: {
      displayName: "Duration",
      className: "",
    },
  },
  {
    element: <OtsAndReachHeader headerInfo={otsHeaderInfo} />,
  },
  {
    element: (
      <OtsAndReachHeader
        headerInfo={reachHeaderInfo}
        subText={`Population: ${getModifiedPopulation(cityPopulation)}`}
      />
    ),
  },
  {
    title: {
      displayName: "Frequency",
      className: "col-1 text-right align-middle",
    },
  },
  {
    title: {
      displayName: "LTS",
      className: "col-1 text-center align-middle",
    },
  },
];

// Page Component
function PlanCityViewMediaRow({ media, cityPopulation }) {
  const dispatch = useDispatch();

  if (!media || Object.keys(media).length === 0) {
    return null;
  }

  const {
    startTimestamp,
    endTimestamp,
    duration,
    campaignId,
    id: mediaId,
    reachFrequency,
  } = media;

  // function to update date
  function onDateSelect(selectedDate) {
    dispatch(updateCampaignMediaDuration(selectedDate, media));
  }

  const dateObj = { startTimestamp, endTimestamp };

  // Redirect Url
  const campaignMediaViewPageUrl = RedirectTo.campaignMediaViewPageUrl
    .replace(":campaignId", campaignId)
    .replace(":mediaId", mediaId);

  return (
    <tr>
      <InfoCell
        mediaElementStyle={"p-2"}
        media={media}
        redirectUrl={campaignMediaViewPageUrl}
      />
      <DurationCell
        dateObj={dateObj}
        durationElementStyle={"p-2 align-middle text-right"}
        onDateSelect={onDateSelect}
        duration={duration}
      />
      <ImpressionsCell impressionsElementStyle="align-middle" media={media} />
      <ReachCell
        reachElementStyle="align-middle"
        reachFrequency={reachFrequency}
        cityPopulation={cityPopulation}
        showPopulation={false}
      />
      <FrequencyCell
        frequencyElementClass={"p-2 text-right align-middle"}
        reachFrequency={reachFrequency}
      />
      <LtsCell ltsElementClass={"p-2 text-center align-middle"} media={media} />
    </tr>
  );
}

function PlanCityViewMediaTable({ mediaList, cityPopulation }) {
  const isMediaSitesPresent = mediaList.length > 0;

  return (
    <div className="table-responsive mt-3">
      <table className="table table-media">
        <TableHeaders
          tableHeaders={tableHeaders(cityPopulation)}
          headerClass={"thead"}
        />

        <tbody>
          {!isMediaSitesPresent && (
            <NoTableDataMessage
              colSpan={6}
              message="City don't have any media sites"
            />
          )}

          {isMediaSitesPresent &&
            mediaList.map((media) => (
              <PlanCityViewMediaRow
                media={media}
                key={media.id}
                cityPopulation={cityPopulation}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
}

function CityViewTableHeading() {
  // update campaignMediaDuration
  const campaignMediaDurationChanging = useSelector(
    (state) => state.campaignMedia.mediaDurationChanging
  );

  return (
    <div className="d-flex mt-4">
      <h4 className="mb-0 mr-2">{"Site Details"}</h4>
      {campaignMediaDurationChanging && (
        <AlertMessage
          className="mb-0 py-1"
          spinnerClassName="spinner-border-sm"
          textMessage="Updating Metrics"
        />
      )}
    </div>
  );
}

/**
 * Page
 */
function CampaignPlanCityviewPage() {
  const dispatch = useDispatch();
  const { campaignId: id, cityId } = useParams();

  // Selector State
  const citySummaryLoading = useSelector(
    (state) => state.campaignCityReport.citySummaryLoading
  );
  const { campaign = {}, summary: citySummary = {} } = useSelector(
    (state) => state.campaignCityReport.citySummary
  );

  const { targetGroupName = "" } = campaign;

  const { population: cityPopulation } = citySummary;

  // CityMedia and Data
  const cityMediaLoading = useSelector(
    (state) => state.campaignMedia.cityLoading
  );
  const cityMediaSummary = useSelector(
    (state) => state.campaignMedia.citySummary
  );
  const cityMediaSummaryResult = cityMediaSummary.result || {};
  const { items = [] } = cityMediaSummaryResult;

  // Link to pass in CampaignSidebar
  const redirectLinkObj = {
    campaignOverviewLink: RedirectTo.campaignPlanOverviewPageUrl.replace(
      ":campaignId",
      id
    ),
    citySummaryLink: RedirectTo.campaignPlanCityViewPageUrl.replace(
      ":campaignId",
      id
    ),
  };

  const cityInfo = {
    cityId: cityId,
    cityName: citySummary?.cityName || cityId,
  };

  // Pagination
  const { pageNumber, pageSize } = DefaultPagination;

  useEffect(() => {
    dispatch(getCityOverview(id, cityId));
    dispatch(getCampaignMediaByCity(id, cityId, pageNumber, pageSize));
  }, [dispatch, id, cityId]);

  // link to city-specific-Map-view
  const campaignPlanMapViewPageUrl = RedirectTo.campaignPlanMapViewPageUrl
    .replace(":campaignId", id)
    .replace(":cityId", cityId);

  const pageTitle =
    campaign.title +
    " :: " +
    cityInfo.cityName +
    " | TargetGroup :: " +
    targetGroupName;

  const pageHeaderAction = (
    <Link
      className="btn btn-primary rounded-lg px-3"
      to={constructRedirectPath(campaignPlanMapViewPageUrl)}
    >
      Select Media on Map
    </Link>
  );

  //checks for page loading
  if (citySummaryLoading || cityMediaLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  return (
    <>
      {/* Sidebar */}
      <CampaignSidebar
        campaign={campaign}
        citySelected={cityInfo}
        redirectLinkObj={redirectLinkObj}
      />

      {/* Main Content */}
      <div className="content-wrapper">
        {/* Page Header */}
        <PageHeader title={pageTitle} actions={pageHeaderAction} />

        {/* Page Content */}
        <div className="page-content">
          {/* Performance Card */}
          <CampaignCityPerformance citySummary={citySummary} />

          {/* CityView Table Header */}
          <CityViewTableHeading />

          {/* Media Table */}
          <PlanCityViewMediaTable
            mediaList={items}
            cityPopulation={cityPopulation}
          />
        </div>
      </div>
    </>
  );
}

export default CampaignPlanCityviewPage;
